<template>
    <cart-template
        :class="{ 'overlay prelative': activeCompare }"
        :advCheck="alertAdv"
        :isSales="true"
        :customer="customer"
    >
        <template #configuration>
            <p class="cart-config-title mb-0">
                {{ $l10n.__('Abonnement de') }} {{ customer.name }}
            </p>
            <p class="mt-1 mb-6 semibold gray-60 fs-3">
                <span>{{ $l10n.__("Date d'activation de l'abonnement") }}</span>
                {{ $filters.dateFormatter(periodApplianceStart, 'SLASHED_DATE', false) }}
            </p>

            <!-- Current Subscription details -->
            <subscription-summary context="upsell" isSales class="mb-5" />

            <el-form
                ref="saleCartForm"
                labelPosition="top"
                :rules="setRules"
                :model="modelForm"
                class="mb-8"
            >
                <date-config store="upsell"></date-config>
                <h1>{{ $l10n.__("Configuration de l'offre") }}</h1>

                <article class="pb-1">
                    <header class="flex-level ai-center mt-6 mb-4">
                        <h2 class="m-0">{{ $l10n.__('Le plan') }}</h2>
                        <a href="https://go.sellsy.com/tarifs/" target="_blank" class="ml-3 link">{{
                            $l10n.__('Comparer les plans')
                        }}</a>
                    </header>
                    <el-form-item class="mb-0">
                        <el-radio-group v-model="licencePlan">
                            <el-radio
                                v-for="plan in plansChoices"
                                :key="plan.code"
                                :disabled="plan.disabled"
                                :label="plan.code"
                            >
                                <tag-label
                                    class="semibold uppercase"
                                    :bgColor="plan.color"
                                    :disabled="plan.disabled"
                                >
                                    <template #label>{{ plan.name }}</template>
                                </tag-label>
                            </el-radio>
                        </el-radio-group>
                    </el-form-item>
                </article>

                <span class="separator block my-6"></span>

                <article>
                    <el-form-item prop="products" class="flex-column-content">
                        <header class="flex-row jc-space ai-center mb-5">
                            <h2 class="m-0">{{ $l10n.__('Les licences') }}</h2>
                            <RadioInput
                                mode="edit"
                                radioStyle="button"
                                :modelValue="recurrency"
                                :choices="recurrenciesChoices"
                                @change="updateRecurrency"
                            />
                        </header>
                        <LicenceProduct
                            v-for="product in availablesProducts"
                            :key="product.code"
                            class="mb-5"
                            :modelValue="products[product.id]"
                            :product="product"
                            :plan="plan"
                            userType="sale"
                            :numformat="numformat"
                            @update:modelValue="updateProductQuantity"
                        >
                        </LicenceProduct>
                        <template #error="{ error }">
                            <div
                                class="absolute wp-10 mt-0 mb-5 alert alert-danger"
                                style="order: -1"
                            >
                                {{ error }}
                            </div>
                        </template>
                    </el-form-item>
                </article>

                <span class="separator block my-6"></span>

                <article>
                    <el-form-item>
                        <header>
                            <h2 class="mb-5">{{ $l10n.__('Modules supplémentaires') }}</h2>
                        </header>
                        <BonusPack
                            v-for="product in [...availablesPacks, ...currentGroupedPacks]"
                            :key="product.code"
                            class="mb-5"
                            :modelValue="products[product.id]"
                            :product="product"
                            :plan="plan"
                            :numformat="numformat"
                            userType="sale"
                            :sourceLimitation="false"
                            @update:modelValue="updateProductQuantity"
                            @changePackPlan="switchPackPlan"
                        >
                        </BonusPack>
                    </el-form-item>
                </article>

                <span class="separator block my-6"></span>

                <article>
                    <header>
                        <h2 class="mt-0 mb-5">{{ $l10n.__('Paiement') }}</h2>
                    </header>
                    <el-form-item :label="$t.__('common.labels.payment_method')">
                        <el-radio-group v-model="payment" prop="paymentMethod">
                            <el-radio
                                v-for="payment in paymentMethods"
                                :key="payment.id"
                                class="black"
                                :label="payment.id"
                                :disabled="disableWithdrawal(payment.id)"
                                >{{ payment.label }}</el-radio
                            >
                        </el-radio-group>
                    </el-form-item>
                </article>

                <span class="separator block my-6"></span>

                <!-- Billing Contact -->
                <SamCartContactForm
                    :modelValue="contact"
                    :errors="errors"
                    @update:modelValue="updateContactForm"
                />

                <span class="separator block my-6"></span>

                <!-- Billing Settings -->
                <h2 class="mt-0 mb-5">{{ $l10n.__('Informations de facturation') }}</h2>

                <!-- Billing Settings -- Local -->
                <address-input v-model="addressSettings">
                    <template #manualAddress="{ data, updateCountry }">
                        <el-form-item
                            prop="billingSettings.line1"
                            :label="$t.__('common.labels.street_name')"
                        >
                            <el-input v-model="data.line1" autocomplete="off"></el-input>
                        </el-form-item>
                        <el-form-item :label="$t.__('common.labels.more_address_informations')">
                            <el-input v-model="data.line2" autocomplete="off"></el-input>
                        </el-form-item>
                        <div class="flex-row">
                            <el-form-item
                                class="wp-3"
                                prop="billingSettings.postalCode"
                                :label="$t.__('common.labels.postal_code')"
                            >
                                <el-input v-model="data.postalCode" autocomplete="off"></el-input>
                            </el-form-item>
                            <el-form-item
                                class="wp-7 ml-3"
                                prop="billingSettings.city"
                                :label="$t.__('common.labels.city')"
                            >
                                <el-input v-model="data.city" autocomplete="off"></el-input>
                            </el-form-item>
                        </div>
                        <div class="flex-row">
                            <el-form-item class="wp-5" :label="$t.__('common.labels.state')">
                                <el-input
                                    v-model="data.administrative"
                                    autocomplete="off"
                                ></el-input>
                            </el-form-item>
                            <el-form-item
                                class="wp-5 ml-3"
                                prop="billingSettings.country"
                                :label="$t.__('common.labels.country')"
                            >
                                <country-input
                                    :defaultValue="{
                                        code: data.countryCode,
                                        name: data.country,
                                    }"
                                    @change="updateCountry"
                                >
                                </country-input>
                            </el-form-item>
                        </div>
                    </template>
                    <template #algoliaWrapper="{ methods, props, data }">
                        <el-form-item
                            prop="billingSettings.line1"
                            :label="$t.__('common.labels.street_name')"
                            :class="{ hide: data.currentValue.manualMode }"
                        >
                            <algolia-input
                                :elementName="props.modelValue._id"
                                placeType="address"
                                @bubble-address="methods.captureAddress"
                            ></algolia-input>
                            <CompassLink
                                :label="props.manuallyLabel"
                                type="primary"
                                @click="methods.switchMode"
                            />
                        </el-form-item>
                    </template>
                </address-input>

                <!-- BillingSettings -- Language -->
                <div class="flex-row">
                    <el-form-item
                        class="wp-5"
                        prop="billingLocale"
                        :label="$t.__('common.labels.invoicing_language')"
                    >
                        <el-select v-model="contact.billingLocale" class="wp-10">
                            <el-option
                                v-for="locale in billingLocales"
                                :key="locale.code"
                                :label="locale.name"
                                :value="locale.code"
                            >
                            </el-option>
                        </el-select>
                    </el-form-item>

                    <!-- BillingSettings -- TVA -->
                    <el-form-item
                        class="wp-5 ml-4"
                        :label="$t.__('common.labels.tax_number')"
                        prop="vatIntra"
                    >
                        <el-input :modelValue="tvaIntra" disabled></el-input>
                    </el-form-item>
                </div>

                <span class="separator block my-6"></span>

                <!-- Custom Message Settings -->
                <h2 class="mt-0 mb-5">{{ $l10n.__('Personnalisation des emails') }}</h2>

                <custom-message v-if="contact.name" context="upsell"></custom-message>
            </el-form>
        </template>
        <template #resume>
            <section class="pabsolute compare aright">
                <el-button
                    class="compare-btn warning-05-bkg warning-100 b-0 px-2 h-32"
                    @click="toggleCompareSheet"
                >
                    <span class="flex-row ai-center jc-center">
                        <i
                            class="mr-1"
                            :class="{
                                'icon-chevron-right': activeCompare,
                                'icon-chevron-left': !activeCompare,
                            }"
                        ></i>
                        <span class="semibold">{{ $l10n.__('Comparer') }}</span>
                    </span>
                </el-button>
                <!-- COMPARE CART WITH VARIATION -->
                <transition name="xslide-fade">
                    <compare-upsell-sheet
                        v-if="activeCompare"
                        class="compare-sheet aleft"
                        store="upsell"
                    >
                    </compare-upsell-sheet>
                </transition>
            </section>
            <cart-summary context="upsell" :userSale="true">
                <template #licences>
                    <ul class="ov-auto mb-0 cart-recap-products">
                        <!-- Products Summary -->
                        <li
                            v-for="(product, index) in productsWithLicences"
                            :key="product.code"
                            class="flex-row"
                            :class="{
                                'mb-to-free-licences':
                                    !freeLicencesByProduct(product.id) &&
                                    previousFreeLicencesByProduct(product.id),
                                'mb-2': index + 1 !== productsWithLicences.length,
                            }"
                        >
                            <div>
                                <p class="grey m-0 fs-3">
                                    {{
                                        $l10n.n__(
                                            '%(total)s licence %(productsName)s',
                                            '%(total)s licences %(productsName)s',
                                            products[product.id].quantity,
                                            {
                                                total: products[product.id].quantity,
                                                productsName: product.title,
                                            },
                                        )
                                    }}
                                </p>
                                <span v-if="freeLicencesByProduct(product.id)" class="grey">{{
                                    $l10n.n__(
                                        'Dont %s offerte',
                                        'Dont %s offertes',
                                        freeLicencesByProduct(product.id),
                                        freeLicencesByProduct(product.id),
                                    )
                                }}</span>
                            </div>
                            <template v-if="products[product.id].total">
                                <p
                                    v-if="freeLicencesByProduct(product.id)"
                                    class="flex-item-right my-0"
                                >
                                    <span class="strike grey semibold">{{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly.total_amount,
                                            numformat,
                                            true,
                                        )
                                    }}</span>
                                    <span class="success-50 fs-3 pl-2 semibold"
                                        >{{
                                            $filters.numberFormatToFloatWithOptions(
                                                products[product.id].total.monthly,
                                                numformat,
                                                true,
                                            )
                                        }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                                    >
                                </p>
                                <p v-else class="flex-item-right my-0 fs-3 semibold grey">
                                    {{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly
                                                .total_amount_discounted,
                                            numformat,
                                            true,
                                        )
                                    }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                </p>
                            </template>
                        </li>
                        <!-- Packs Summary -->
                        <li
                            v-for="(product, index) in [
                                ...packsWithLicences,
                                ...groupedPacksWithLicences,
                            ]"
                            :key="product.code"
                            class="flex-row"
                            :class="{
                                'mb-2':
                                    index + 1 !==
                                    [...packsWithLicences, ...groupedPacksWithLicences].length,
                                'mt-2': index === 0,
                            }"
                        >
                            <div>
                                <p class="grey m-0 fs-3">
                                    {{
                                        $l10n.__('Module %(productsName)s', {
                                            productsName: !!product.groups.length
                                                ? product.printableName
                                                : product.title,
                                        })
                                    }}
                                </p>
                            </div>
                            <template v-if="products[product.id].total">
                                <!-- Discount detail -->
                                <p
                                    v-if="
                                        products[product.id].total.monthly.total_amount_discounted <
                                        products[product.id].total.monthly.total_amount
                                    "
                                    class="flex-item-right my-0 semibold"
                                >
                                    <span class="strike grey semibold">{{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly.total_amount,
                                            numformat,
                                            true,
                                        )
                                    }}</span>
                                    <span class="success-50 fs-3 pl-2">
                                        <template
                                            v-if="
                                                products[product.id].total.monthly
                                                    .total_amount_discounted !== 0
                                            "
                                        >
                                            {{
                                                $filters.numberFormatToFloatWithOptions(
                                                    products[product.id].total.monthly
                                                        .total_amount_discounted,
                                                    numformat,
                                                    true,
                                                )
                                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                        </template>
                                        <template v-else>{{ $l10n.__('Offert') }}</template>
                                    </span>
                                </p>
                                <!-- Base price -->
                                <p v-else class="flex-item-right my-0 fs-3 semibold grey">
                                    {{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly
                                                .total_amount_discounted,
                                            numformat,
                                            true,
                                        )
                                    }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                </p>
                            </template>
                        </li>
                    </ul>
                </template>
                <template #subtotal>
                    <div class="flex-column fs-3 cart-recap-total-label">
                        <span class="mb-2">{{ $l10n.__('Total HT avant remise') }}</span>
                        <span class="mb-2">{{ $l10n.__('Remise') }}</span>
                        <span class="mb-2">{{ $l10n.__('Total HT') }}</span>
                        <span v-if="taxRate && taxRate !== 0" class="mb-2"
                            >{{ $l10n.__('TVA') }} {{ taxRate }}%</span
                        >
                    </div>
                    <div class="flex-column aright fs-3">
                        <span class="mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span></span
                        >
                        <span class="mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_discount,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span></span
                        >
                        <span class="mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_discounted,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span></span
                        >
                        <span v-if="taxRate && taxRate !== 0" class="mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_vat,
                                    numformat,
                                    true,
                                )
                            }}
                        </span>
                    </div>
                    <div class="flex-column aright fs-3">
                        <span class="mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                        >
                        <span class="mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_discount,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                        >
                        <span class="mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_discounted,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                        >
                        <span v-if="taxRate && taxRate !== 0" class="mb-2">{{
                            $filters.numberFormatToFloatWithOptions(
                                totalMonthly.total_amount_vat,
                                numformat,
                                true,
                            )
                        }}</span>
                    </div>
                </template>
                <template #total>
                    <div class="flex-row jc-space">
                        <p class="fs-4 my-0 bold cart-recap-total-label">
                            {{ $l10n.__('Total TTC') }}
                        </p>
                        <p class="fs-4 my-0 aright bold flex-row baseline">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_tax_included,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span>
                        </p>
                        <p class="fs-4 my-0 aright bold flex-row baseline">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_tax_included,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                        </p>
                    </div>
                </template>
                <template #actions>
                    <CompassButton
                        :label="$t.__('common.actions.send_to_client')"
                        type="primary"
                        size="medium"
                        :disabled="upsellIsLowest || computeLoading"
                        :loading="computeLoading"
                        class="wp-10 mt-4"
                        @click="submitForm('saleCartForm', 'action')"
                    />
                </template>
            </cart-summary>
        </template>
    </cart-template>
</template>

<script>
import Address from 'vcomponents/inputs/custom/Address.vue';
import PricingV4Module from 'stores/sellsyaccountmanager/v4/upsell';
import AddressRules from 'vcomponents/forms/rules/AddressRules.js';
import CountryInput from 'vcomponents/inputs/CountryInput.vue';
import AlgoliaInput from 'vcomponents/inputs/AlgoliaInput.vue';
import { createNamespacedHelpers } from 'vuex';
import VatIdentifierValidator from 'Services/VatIdentifierValidator';
import TagLabel from 'vcomponents/utilities/tags/TagLabel.vue';
import LicenceProduct from 'vcomponents/pages/account/LicenceProduct.vue';
import BonusPack from 'vcomponents/pages/account/BonusPack.vue';
import FormMixin from 'mixins/FormMixins';
import { fetchMetadata } from 'api/sellsyaccountmanager';
import SubscriptionSummary from './SubscriptionSummary.vue';

import RadioInput from 'vcomponents/inputs/RadioInput.vue';
import SamCartContactForm from 'vcomponents/pages/account/SamCartContactForm.vue';
import CartTemplate from 'vcomponents/pages/account/CartTemplate.vue';
import CartSummary from 'vcomponents/pages/account/CartSummary.vue';
import Card from 'vcomponents/Card.vue';
import CompareUpsellSheet from 'vcomponents/pages/account/upsell/CompareUpsellSheet.vue';
import DateConfig from 'vcomponents/pages/account/sale/DateConfig.vue';
import { goToUrl } from 'js/vue/helpers';
import Loader from 'vcomponents/utilities/Loader.vue';
import CustomMessage from 'vcomponents/pages/account/sale/CustomMessage.vue';

const upsellCart = createNamespacedHelpers('upsellCart');
const corp = createNamespacedHelpers('corp');
export default {
    mixins: [FormMixin],
    data() {
        return {
            rules: {
                name: [{ required: true, message: this.$l10n.__('Veuillez renseigner un nom') }],
                forename: [
                    { required: true, message: this.$l10n.__('Veuillez renseigner un prénom') },
                ],
                phone: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner un numéro de téléphone'),
                    },
                    {
                        min: 4,
                        message: this.$l10n.__('Veuillez renseigner un numéro de téléphone valide'),
                    },
                ],
                billingLocale: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner une langue de facturation'),
                    },
                ],
                email: [
                    {
                        required: true,
                        type: 'email',
                        message: this.$l10n.__('Veuillez renseigner une adresse email'),
                    },
                ],
                billingSettings: {
                    ...AddressRules,
                },
                vatIntra: [
                    {
                        validator: (rule, value, callback) => {
                            /**
                             * Check TVA Intra format with associated country, but no require.
                             */
                            if (
                                value === '' ||
                                _.isNil(value) ||
                                _.isUndefined(this.billingSettings.countryCode)
                            )
                                return callback();

                            if (_.isNull(this.billingSettings.countryCode))
                                return callback(new Error(this.$l10n.__('Aucun pays sélectionné')));

                            const validator = new VatIdentifierValidator(
                                this.billingSettings.countryCode,
                            );
                            if (!validator.isValid(value)) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Le format du n° de tva ne correspond pas à celui du pays sélectionné',
                                        ),
                                    ),
                                );
                            }
                            callback();
                        },
                        trigger: 'change',
                    },
                ],
                products: [
                    {
                        validator: (rule, value, callback) => {
                            /**
                             * Cart must get 2 paid licences or 1 if allowed by permission
                             */
                            if (this.customer.allowsOneLicense && this.quantityPaidLicences < 1) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Veuillez sélectionner au minimum 1 licence payante',
                                        ),
                                    ),
                                );
                            } else if (
                                !this.customer.allowsOneLicense &&
                                this.quantityPaidLicences < 2
                            ) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Veuillez sélectionner au minimum 2 licences payantes',
                                        ),
                                    ),
                                );
                            }

                            callback();
                        },
                        trigger: 'change',
                    },
                ],
                paymentMethod: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner un moyen de paiement'),
                    },
                ],
            },
            customMessageValidator: {
                customMessage: [
                    {
                        validator: (rule, value, callback) => {
                            const customMsg = this.customMessage.match(/<p>(.*?)<\/p>/g);
                            if (!customMsg || customMsg[0].trim() === '<p></p>') {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Ce champ ne peut pas être vide lorsque vous indiquez vouloir joindre un message personnalisé',
                                        ),
                                    ),
                                );
                            }
                            callback();
                        },
                    },
                ],
            },
            activeCompare: false,
        };
    },
    created() {
        this.$store.registerModule('upsellCart', PricingV4Module);
        this.setCustomerId(this.$route.params.customerId);
        this.setSource('sales');

        if (this.$route.params.id !== 'new') {
            this.fetchCart(this.$route.params.id);
        }
    },

    computed: {
        ...corp.mapGetters(['corpName']),
        ...upsellCart.mapGetters([
            'modelForm',
            'contact',
            'billingLocales',
            'periodApplianceStart',
            'billingSettings',
            'recurrencies',
            'recurrency',
            'vatIntra',
            'customer',
            'plans',
            'currentPlan',
            'previousCurrentPlan',
            'availablesProducts',
            'availablesPacks',
            'currentGroupedPacks',
            'products',
            'productsWithLicences',
            'packsWithLicences',
            'groupedPacksWithLicences',
            'previousCart',
            'plan',
            'isYearly',
            'isMonthly',
            'freeLicencesByProduct',
            'previousFreeLicencesByProduct',
            'totalYearly',
            'totalMonthly',
            'computeLoading',
            'taxRate',
            'paymentMethod',
            'paymentMethods',
            'labelPlan',
            'quantityPaidLicences',
            'cartId',
            'numformat',
            'canSubmitUpsell',
            'upsellIsLowest',
            'alertAdv',
            'isADV',
            'advAlertAlreadyShown',
            'isCustomMessageUsed',
            'customMessage',
        ]),
        plansChoices: {
            get() {
                return this.plans.map((plan) => {
                    return {
                        ...plan,
                        disabled:
                            plan.code === 'ESSENTIAL' && this.previousCurrentPlan !== 'ESSENTIAL',
                    };
                });
            },
            set() {
                return;
            },
        },
        recurrenciesChoices: {
            get() {
                return this.recurrencies.map((recurrency) => {
                    return {
                        ...recurrency,
                        disabled: recurrency.value !== this.recurrency,
                    };
                });
            },
            set() {
                return;
            },
        },
        addressSettings: {
            get() {
                return this.billingSettings;
            },
            set(value) {
                this.$store.commit('upsellCart/setBillingSettings', value);
                this.$store.dispatch('upsellCart/computeCart');
            },
        },
        tvaIntra: {
            get() {
                return this.vatIntra;
            },
            set(value) {
                this.$store.commit('upsellCart/setVatIntra', value);
                this.$store.dispatch('upsellCart/computeCart');
            },
        },
        licencePlan: {
            get() {
                return this.currentPlan;
            },
            set(value) {
                this.$store.dispatch('upsellCart/updatePlan', value);
            },
        },
        labelPlan() {
            return this.isYearly
                ? this.$l10n.__('Abonnement annuel')
                : this.$l10n.__('Abonnement mensuel');
        },
        payment: {
            get() {
                return this.paymentMethod;
            },
            set(value) {
                this.$store.commit('upsellCart/setPaymentMethod', value);
            },
        },
        leftDurationSubscription() {
            const serviceEnd = moment(this.previousCart.serviceEndDate);
            const now = moment();
            const months = serviceEnd.diff(now, 'months');
            now.add(months, 'months');
            const days = serviceEnd.diff(now, 'days');

            return this.$l10n.__(`dans ${months} mois et ${days} jours`);
        },
        /**
         * Add custom message if messageUsed's value is true
         */
        setRules() {
            if (this.isCustomMessageUsed) {
                const newRules = Object.assign({ ...this.rules }, this.customMessageValidator);
                return newRules;
            } else {
                return this.rules;
            }
        },
    },
    methods: {
        ...upsellCart.mapMutations(['setCustomerId', 'setSource', 'setAdvAlertVisibility']),
        ...upsellCart.mapActions([
            'fetchMetadata',
            'fetchCustomerData',
            'updateContact',
            'updateRecurrency',
            'updatePlan',
            'updateProductQuantity',
            'computeCart',
            'createCart',
            'fetchCart',
            'updateCart',
            'setMetadata',
            'switchPackPlan',
        ]),
        updateContactForm(e) {
            this.updateContact({
                ...this.contact,
                ...e,
            });
        },
        back() {
            history.back();
        },
        action() {
            if (!this.isADV && !this.canSubmitUpsell && !this.advAlertAlreadyShown) {
                this.setAdvAlertVisibility(true);
                return;
            }

            if (this.$route.params.id !== 'new' || !_.isNil(this.cartId)) {
                // Update
                this.update();
            } else {
                this.create();
            }
        },
        create() {
            this.createCart()
                .then((res) => {
                    Push.success(this.$l10n.__('Panier envoyé avec succès'));
                    goToUrl(
                        this.$router.resolve({
                            name: 'third.overview.samCarts',
                            params: { type: 'client', id: this.$route.params.thirdId },
                        }).href,
                    );
                })
                .catch((errors) => {
                    this.errors.setErrors(errors);
                });
        },
        update() {
            this.updateCart(this.cartId)
                .then(() => {
                    Push.success(this.$l10n.__('Panier modifié avec succès'));
                    goToUrl(
                        this.$router.resolve({
                            name: 'third.overview.samCarts',
                            params: { type: 'client', id: this.$route.params.thirdId },
                        }).href,
                    );
                })
                .catch((errors) => {
                    this.errors.setErrors(errors);
                });
        },
        disableWithdrawal(id) {
            return id === 'manual' && this.isMonthly;
        },
        toggleCompareSheet() {
            this.activeCompare = !this.activeCompare;
            const body = document.getElementById('sellsy_app');
            this.activeCompare
                ? body.classList.add('ov-hidden')
                : body.classList.remove('ov-hidden');
        },
    },
    async beforeRouteEnter(to, from, next) {
        await fetchMetadata(to.params.customerId, 'upsale', 'saas')
            .then((res) => {
                next((vm) => {
                    vm.setMetadata(res);
                    vm.fetchCustomerData(to.params.corpId);
                });
            })
            .catch((err) => {
                next(false);
            });
    },
    beforeUnmount() {
        const body = document.getElementById('sellsy_app');
        body.classList.remove('mr-3', 'ov-hidden');
        this.$store.unregisterModule('upsellCart');
    },
    components: {
        SamCartContactForm,
        'address-input': Address,
        AlgoliaInput,
        CountryInput,
        RadioInput,
        TagLabel,
        LicenceProduct,
        CartTemplate,
        CartSummary,
        Card,
        CompareUpsellSheet,
        BonusPack,
        Loader,
        SubscriptionSummary,
        DateConfig,
        CustomMessage,
    },
};
</script>

<style lang="scss">
@use '~@sellsy/styleguide/src/_variables' as *;
.page-sam-v4-sale-upsell-cart-form {
    .amount {
        align-items: baseline;
        display: flex;
    }

    .cart-recap-products {
        min-height: 180px;
        li {
            margin-bottom: 8px;
            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    .mb-to-free-licences {
        margin-bottom: 22px !important;
    }
}

.el-form-item.flex-column-content {
    .el-form-item__content {
        display: flex;
        flex-direction: column;
    }
}

.cart.overlay .cart-config {
    &:before {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        background: $white;
        opacity: 0.9;
        z-index: 2;
    }
}

.cart-recap {
    .compare {
        right: 100%;
        top: 48px;

        &-btn,
        &-sheet {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        }
    }
    .xslide-fade-enter,
    .xslide-fade-leave-to {
        transform: translateX(100%);
        flex: 0;
        opacity: 0;
    }
    .xslide-fade-enter-active,
    .xslide-fade-leave-active {
        transition: all 700ms ease;
    }
}
#sellsy_app:has(.compare-sheet) {
    overflow: hidden;
}
</style>
